import { useQuery } from "@apollo/client";
import { Combobox } from "@/components/Combobox";
import { graphql, type ResultOf } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { removeNothings } from "@/lib/utils/arrays";

type Props = {
  id?: string;
  selected: string[];
  onSelect: (values: string[]) => void;
  className?: string;
};

export type SourceCityData = ResultOf<typeof sourceCitiesQuery>;
type CityNode = NonNullable<
  NonNullable<NonNullable<SourceCityData>["supplyChainLotSourceCities"]>["edges"][number]
>["node"];

export function SourceCityPicker({ id, selected, onSelect, className }: Props) {
  const { activeBusinessUnitId } = useActiveBusinessUnit();

  const { data, loading } = useQuery(sourceCitiesQuery, {
    variables: { businessUnitId: activeBusinessUnitId! },
    skip: !activeBusinessUnitId,
  });

  const options = getCityOptions(
    removeNothings(data?.supplyChainLotSourceCities?.edges?.map((edge) => edge?.node) ?? []),
  );

  return (
    <Combobox<string>
      id={id}
      name="source-cities"
      options={options}
      selected={selected}
      onChange={onSelect}
      loading={loading}
      type="multi"
      className={className}
    />
  );
}

export const sourceCitiesQuery = graphql(`
  query SupplyChainLotSourceCities($businessUnitId: ID!) {
    supplyChainLotSourceCities(businessUnit: $businessUnitId) {
      edges {
        node {
          city
        }
      }
    }
  }
`);

export const getCityOptions = (cityNodes: CityNode[]) => {
  const uniqueCities = Array.from(new Set(cityNodes.map((node) => node.city.trim()).filter(Boolean))).map((city) => ({
    label: city,
    value: city,
  }));

  return uniqueCities;
};
